import React, { useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Link, router, useForm } from '@inertiajs/react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type Resource = {
  id?: number;
  title: string;
  url: string;
  pdf?: { name: string };
  description: string;
  category: string;
  position: number;
  featured: boolean;
};

type Props = {
  resource: Resource;
  existing_categories: string[];
  errors?: string[];
};

export default function ResourceForm({ resource, existing_categories, errors }: Props) {
  const [useNewCategory, setUseNewCategory] = useState(false);
  const { data, setData, processing } = useForm({
    title: resource.title || '',
    url: resource.url || '',
    pdf: resource.pdf || { filename: '', byte_size: '0 KB' },
    description: resource.description || '',
    category: resource.category || '',
    position: resource.position || 0,
    featured: resource.featured || false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (resource.id) {
      router.patch(`/admin/resources/${resource.id}`, { resource: data });
    } else {
      router.post('/admin/resources', { resource: data });
    }
  };


  const suggestedCategories = [
    'General Resources',
    'Mental Health',
    'Medical School',
    'Graduate School',
    'CV & Resume',
    'Personal Statements',
    'Interview Prep',
    'Financial Aid',
    'Research Opportunities',
    'Test Preparation'
  ];
  const availableCategories = [...new Set([...existing_categories, ...suggestedCategories])].sort();

  return (
    <AdminLayout title={resource.id ? 'Edit Resource' : 'New Resource'}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            href="/admin/resources"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Resources
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-gray-900">
            {resource.id ? 'Edit Resource' : 'New Resource'}
          </h1>
        </div>

        {/* Error messages */}
        {errors && errors.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm font-medium text-red-800">Please fix the following errors:</p>
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white shadow rounded-lg p-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                type="text"
                value={data.title}
                onChange={(e) => setData('title', e.target.value)}
                required
                className="mt-1"
                placeholder="e.g., MCAT Study Guide"
              />
            </div>

            <div>
              <Label htmlFor="url">URL *</Label>
              <Input
                id="url"
                type="url"
                value={data.url}
                onChange={(e) => setData('url', e.target.value)}
                className="mt-1"
                placeholder="https://example.com/resource"
              />
            </div>

            <div>
              <Label>Attach PDF</Label>
              <div className='flex gap-3 mt-2 items-center'>
                <Input
                  id='pdf'
                  type='file'
                  accept='.pdf'
                  className='mt-1'
                  style={{ display: 'none' }}
                  onChange={e => setData('pdf', e.target.files ? e.target.files[0] : null)}
                />
                <Button type='button' variant={'outline'} size={'sm'}><label htmlFor='pdf' className='block'>Choose File</label></Button>
                <span>{data.pdf?.name ? data.pdf.name : 'No Chosen File'}</span>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={data.description}
                onChange={(e) => setData('description', e.target.value)}
                rows={3}
                className="mt-1"
                placeholder="Brief description of this resource..."
              />
            </div>

            <div>
              <Label htmlFor="category">Category *</Label>
              <div className="mt-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="new-category"
                    checked={useNewCategory}
                    onCheckedChange={(checked) => {
                      setUseNewCategory(checked as boolean);
                      if (!checked) setData('category', '');
                    }}
                  />
                  <Label htmlFor="new-category" className="text-sm font-normal">
                    Create new category
                  </Label>
                </div>

                {useNewCategory ? (
                  <Input
                    type="text"
                    value={data.category}
                    onChange={(e) => setData('category', e.target.value)}
                    placeholder="Enter new category name"
                    required
                  />
                ) : (
                  <Select
                    value={data.category}
                    onValueChange={(value) => setData('category', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label htmlFor="position">Position (Order)</Label>
                <Input
                  id="position"
                  type="number"
                  value={data.position}
                  onChange={(e) => setData('position', parseInt(e.target.value) || 0)}
                  className="mt-1"
                  min="0"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Lower numbers appear first
                </p>
              </div>

              <div>
                <Label htmlFor="featured" className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    id="featured"
                    checked={data.featured}
                    onCheckedChange={(checked) => setData('featured', checked as boolean)}
                  />
                  <span>Featured Resource</span>
                </Label>
                <p className="mt-1 text-xs text-gray-500">
                  Featured resources are highlighted
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button type="button" variant="outline" asChild>
              <Link href="/admin/resources">Cancel</Link>
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? 'Saving...' : 'Save Resource'}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
